<template>
  <v-container fluid>
    <v-layout>
      <v-flex xs12>
        <v-data-table
          class="incentive-firings-table"
          locale="pt-br"
          no-data-text="Nenhum dado encontrado"
          :headers="headers"
          :pagination.sync="pagination"
          :total-items="totalItems"
          :items="tableSteps"
          :rows-per-page-items="pagination.rowsPerPageItems"
          expand
          item-key="step"
        >
          <!-- linhas primeira camada: steps (etapa) -->
          <template v-slot:items="props">
            <IncentiveFiringsTableRow
              :item="props.item"
              :level="1"
              @click="props.expanded = !props.expanded"
            />
          </template>

          <!-- tabela segunda camada: approach_type (tipos de abordagem) -->
          <template v-slot:expand="props">
            <v-data-table
              class="incentive-firings-table"
              locale="pt-br"
              no-data-text="Nenhum dado encontrado"
              :headers="headers"
              :items="tablesApproachType[props.item.step]"
              expand
              hide-headers
              hide-actions
              item-key="approach_type"
            >
              <!-- linhas segunda camada: approach_type (tipos de abordagem) -->
              <template v-slot:items="approachProps">
                <IncentiveFiringsTableRow
                  :item="approachProps.item"
                  :level="2"
                  @click="approachProps.expanded = !approachProps.expanded"
                />
              </template>

              <!-- tabela terceira camada: messages (mensagem) -->
              <template v-slot:expand="approachProps">
                <v-data-table
                  class="incentive-firings-table"
                  locale="pt-br"
                  rows-per-page-text="Mostrar"
                  no-data-text="Nenhum dado encontrado"
                  :headers="headers"
                  :total-items="totalItems"
                  :items="
                    tableMessages[props.item.step][
                      approachProps.item.approach_type
                    ]
                  "
                  hide-headers
                  hide-actions
                >
                  <template v-slot:items="props">
                    <IncentiveFiringsTableRow :item="props.item" :level="3" />
                  </template>
                </v-data-table>
              </template>
            </v-data-table>
          </template>

          <template v-slot:no-data>
            <v-layout column align-center justify-center class="pa-5" style="min-height: 400px;">
              <v-progress-circular
                v-if="loading"
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>

              <template v-else>
                <div class="empty-state">
                  <v-icon size="80" :color="creditor ? '#FF9800' : '#BDBDBD'" class="mb-4">
                    {{ creditor ? 'mdi-chart-timeline-variant' : 'mdi-filter-variant' }}
                  </v-icon>
                  <div class="empty-state-title">
                    {{ creditor ? 'Nenhum disparo de incentivo encontrado' : 'Selecione um credor para começar' }}
                  </div>
                  <div class="empty-state-subtitle">
                    {{ creditor ? 'Não há dados de incentivos para o período e filtros selecionados.' : 'Escolha um credor e período para visualizar os disparos de incentivos.' }}
                  </div>
                  <v-btn 
                    v-if="creditor" 
                    color="primary" 
                    outlined 
                    class="mt-4"
                    @click="$emit('clear-filters')"
                  >
                    <v-icon left>mdi-filter-remove</v-icon>
                    Limpar Filtros
                  </v-btn>
                </div>
              </template>
            </v-layout>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import strategyPublic from "@/utils/strategy-public";
import IncentiveFiringsTableRow from "./IncentiveFiringsTableRow.vue";

export default {
  name: "IncentiveFiringsTable",
  components: {
    IncentiveFiringsTableRow,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    creditor: {
      type: Number | null,
      default: null,
    },
  },
  data() {
    return {
      headers: [
        { text: "Etapa", value: "step", width: "15%", sortable: false },
        { text: "Canal", value: "approach_type", width: "10%", sortable: false },
        { text: "Abordagem", value: "message", width: "35%", sortable: false },
        { text: "Notificações", value: "notifications", width: "10%", sortable: false },
        { text: "Acesso", value: "accessed", width: "10%", sortable: false },
        { text: "Login", value: "localized", width: "10%", sortable: false },
        { text: "Confirmada", value: "proposals_confirmed", width: "10%", sortable: false },
      ],
      pagination: {
        rowsPerPage: 10,
        rowsPerPageItems: [10, 25, 50, 100],
      },
      public: strategyPublic.strategyPublic,
      tableSteps: [], // primeira
      tablesApproachType: {}, // segundas
      tableMessages: {}, // terceiras
    };
  },
  watch: {
    items: {
      deep: true,
      handler(newItems) {
        // Sempre limpar primeiro
        this.tableSteps = [];
        this.tablesApproachType = {};
        this.tableMessages = {};
        
        // Só processar se houver dados
        if (newItems && newItems.length > 0) {
          this.extractTableData(newItems);
        }
      },
    },
  },
  methods: {
    extractTableData(data) {
      this.tableSteps = [];
      this.tablesApproachType = {};
      this.tableMessages = {};
      
      const steps = new Set();

      data.forEach((item) => {
        if (item.step) steps.add(item.step);
      });

      if (steps.size > 0) {
        steps.forEach((step) => {
          const stepData = data.filter((t) => t.step === step);
          const stepTotals = stepData.reduce((acc, item) => {
            acc.accessed += item.accessed || 0;
            acc.approach_cost += (item.approach_cost || 0) * (item.notifications || 0);
            acc.localized += item.localized || 0;
            acc.notifications += item.notifications || 0;
            acc.proposals_confirmed += item.proposals_confirmed || 0;
            return acc;
          }, {
            accessed: 0,
            approach_cost: 0,
            localized: 0,
            notifications: 0,
            proposals_confirmed: 0,
          });

          this.tableSteps.push({
            accessed: stepTotals.accessed,
            approach_cost: stepTotals.approach_cost,
            approach_type: "",
            creditor_id: this.creditor,
            date: "",
            gateway_id: "",
            localized: stepTotals.localized,
            message: "",
            notifications: stepTotals.notifications,
            proposals_confirmed: stepTotals.proposals_confirmed,
            step: step,
            strategy_public: "",
          });

          const approachTypesAvailable = new Set();

          data
            .filter((t) => t.step === step)
            .forEach((item) => {
              if (item.approach_type) {
                approachTypesAvailable.add(item.approach_type);
              }
            });

          approachTypesAvailable.forEach((approachType) => {
            this.tablesApproachType[step] = this.tablesApproachType[step] || [];
            this.tableMessages[step] = this.tableMessages[step] || {};
            
            const approachData = data.filter((t) => t.step === step && t.approach_type === approachType);
            const approachTotals = approachData.reduce((acc, item) => {
              acc.accessed += item.accessed || 0;
              acc.approach_cost += (item.approach_cost || 0) * (item.notifications || 0);
              acc.localized += item.localized || 0;
              acc.notifications += item.notifications || 0;
              acc.proposals_confirmed += item.proposals_confirmed || 0;
              return acc;
            }, {
              accessed: 0,
              approach_cost: 0,
              localized: 0,
              notifications: 0,
              proposals_confirmed: 0,
            });
            
            this.tablesApproachType[step].push({
              accessed: approachTotals.accessed,
              approach_cost: approachTotals.approach_cost,
              approach_type: approachType,
              creditor_id: this.creditor,
              date: "",
              gateway_id: "",
              localized: approachTotals.localized,
              message: "",
              notifications: approachTotals.notifications,
              proposals_confirmed: approachTotals.proposals_confirmed,
              step: step,
              strategy_public: "",
            });
            
            this.tableMessages[step][approachType] = data.filter((t) => {
              return t.step === step && t.approach_type === approachType;
            });
          });
        });
      }
    },
  },
};
</script>

<style scoped>
.incentive-firings-table >>> table {
  table-layout: fixed;
  width: 100%;
}

/* Force specific column widths for all tables */
.incentive-firings-table >>> th:nth-child(1),
.incentive-firings-table >>> td:nth-child(1) {
  width: 15% !important;
  min-width: 15% !important;
  max-width: 15% !important;
}

.incentive-firings-table >>> th:nth-child(2),
.incentive-firings-table >>> td:nth-child(2) {
  width: 10% !important;
  min-width: 10% !important;
  max-width: 10% !important;
}

.incentive-firings-table >>> th:nth-child(3),
.incentive-firings-table >>> td:nth-child(3) {
  width: 35% !important;
  min-width: 35% !important;
  max-width: 35% !important;
}

.incentive-firings-table >>> th:nth-child(4),
.incentive-firings-table >>> td:nth-child(4) {
  width: 10% !important;
  min-width: 10% !important;
  max-width: 10% !important;
}

.incentive-firings-table >>> th:nth-child(5),
.incentive-firings-table >>> td:nth-child(5) {
  width: 10% !important;
  min-width: 10% !important;
  max-width: 10% !important;
}

.incentive-firings-table >>> th:nth-child(6),
.incentive-firings-table >>> td:nth-child(6) {
  width: 10% !important;
  min-width: 10% !important;
  max-width: 10% !important;
}

.incentive-firings-table >>> th:nth-child(7),
.incentive-firings-table >>> td:nth-child(7) {
  width: 10% !important;
  min-width: 10% !important;
  max-width: 10% !important;
}

/* Remove padding from expanded content */
.incentive-firings-table >>> .v-datatable__expand-content {
  padding: 0 !important;
}

/* Critical: remove padding from the TD that wraps nested tables */
.incentive-firings-table >>> tr.v-datatable__expand-row > td {
  padding: 0 !important;
}

/* Remove box-shadow from nested tables */
.incentive-firings-table >>> .v-datatable__expand-content .v-datatable {
  box-shadow: none !important;
}

/* Ensure nested tables have fixed layout */
.incentive-firings-table >>> .v-datatable__expand-content table {
  table-layout: fixed;
  width: 100%;
}

/* Remove wrapper margins that could shift nested tables */
.incentive-firings-table >>> .v-datatable__expand-content .v-datatable__wrapper {
  margin: 0 !important;
  padding: 0 !important;
}

/* Normalize all cell padding to ensure alignment */
.incentive-firings-table >>> th,
.incentive-firings-table >>> td {
  padding-left: 16px !important;
  padding-right: 16px !important;
}

.empty-state {
  text-align: center;
  max-width: 500px;
  padding: 40px 20px;
}

.empty-state-title {
  font-size: 24px;
  font-weight: 600;
  color: #424242;
  margin-bottom: 12px;
  line-height: 1.4;
}

.empty-state-subtitle {
  font-size: 16px;
  color: #757575;
  line-height: 1.6;
}
</style>
